import { PageTitle } from "../../../_metronic/layout/core"
import { useNavigate, useSearchParams } from "react-router-dom"
import { RoutePath } from "../../routing/routes";
import { useCallback, useMemo, useState } from "react";
import { InputSearch } from "../../components/InputSearch";
import { getFavorites, getHistory, getAvailableStores } from "../../api/copiloto-do-vendedor";
import PreloaderIcon from "../../components/PreloaderIcon";
import { formatCNPJ } from "../../utils/format";
import { useEffectOnce } from "../../hooks/useEffectOnce";
import { StoreCard } from "../../components/StoreCard/StoreCard";
import { RawStoreInfo } from "../../types/RawStoreInfo";
import { RawStoreSearchResult } from "../../types/RawStoreSearchResult";
import { MapPin, Mail, Search, Store as StoreIcon, History, Star, ChevronDown, ChevronUp, Building2, TrendingUp } from 'lucide-react';

function EmptyState({ icon: Icon, title, description }: { icon: React.ElementType; title: string; description: string }) {
    return (
      <div className="py-8 flex flex-col items-center text-center">
        <div className="p-4 bg-gray-50 rounded-full mb-4">
          <Icon className="h-6 w-6 text-gray-400" />
        </div>
        <h4 className="text-sm font-semibold text-gray-900 mb-1">{title}</h4>
        <p className="text-xs text-gray-500 max-w-xs">{description}</p>
      </div>
    );
}

function StoreListItem({ store, onClick }: { store: RawStoreSearchResult; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full px-4 py-3 hover:bg-gray-50 transition-colors duration-150 border-b border-gray-100 last:border-0 text-left group"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center group-hover:bg-blue-100 transition-colors">
            <StoreIcon className="h-5 w-5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600 transition-colors">
              {store.RazaoSocial}
            </p>
            
            <p className="text-xs font-medium text-gray-600 truncate group-hover:text-blue-600 transition-colors">
              {store.NomeFantasia}
            </p>
            <p className="text-xs text-gray-500">{formatCNPJ(store.CNPJLojista)}</p>
          </div>
          <div className="flex items-center text-xs text-gray-500">
              <MapPin className="h-3.5 w-3.5 mr-1 text-gray-400" />
              <span className="truncate">{store.Cidade} / {store.Estado}</span>
            </div>
            {store.Email && (
              <div className="flex items-center text-xs text-gray-500">
                <Mail className="h-3.5 w-3.5 mr-1 text-gray-400" />
                <span className="truncate">{store.Email}</span>
              </div>
            )}
        </div>
        <div className="flex-shrink-0 ml-4">
          <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
            <ChevronDown className="h-4 w-4 text-gray-400 group-hover:text-blue-600 transform -rotate-90" />
          </div>
        </div>
      </div>
    </button>
  );
}

export const CopilotoDoVendedorPesquisa = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [results, setResults] = useState<RawStoreSearchResult[]>([]);
    const [stores, setStores] = useState<RawStoreSearchResult[]>([]);
    const [history, setHistory] = useState<RawStoreInfo[]>([]);
    const [favorites, setFavorites] = useState<RawStoreInfo[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [showAllStores, setShowAllStores] = useState<boolean>(false);

    const cnpj = useMemo(() => {
        return searchParams.get('cnpj');
    }, [searchParams]);

    const title = useMemo(() => {
        let _title = `Copiloto do Vendedor > Pesquisa`;
        if (cnpj) {
            _title += ` > ${cnpj}`;
        }
        return _title;
    }, [cnpj]);

    const displayedStores = useMemo(() => {
        return showAllStores ? stores : stores.slice(0, 20);
    }, [stores, showAllStores]);

    const hasMoreStores = useMemo(() => {
        return stores.length > 20;
    }, [stores]);

    const handleSearch = useCallback((query: string) => {
      if (query.length < 1) {
        setResults([]);
        return;
      }
      const normalizedQuery = query.toLowerCase().trim();
      const filteredStores: RawStoreSearchResult[] = [];
      for (let i = 0; i < stores.length; i++) {
        const store = stores[i];
        if (
          store.NomeFantasia.toLowerCase().includes(normalizedQuery) ||
          store.CNPJLojista.includes(query)
        ) {
          filteredStores.push(store);
          if (filteredStores.length >= 50) {
            break;
          }
        }
      }
      setResults(filteredStores);
    }, [stores]);

    const handleSelect = useCallback((value:RawStoreSearchResult | RawStoreInfo) => {
        const path:RoutePath = '/copiloto-do-vendedor/lojista';
        navigate(`${path}/${value.CNPJLojista}`);
    }, [navigate]);

    const loadData = useCallback(async () => {
        const [historyRes, favoritesRes, availableStores] = await Promise.all([
          getHistory(),
          getFavorites(),
          getAvailableStores()
        ]);
        setFavorites(favoritesRes.data || []);
        setHistory(historyRes.data || []);
        setStores(availableStores.data || []);
        setIsLoading(false);
    }, []);

    useEffectOnce(() => {
        loadData();
    });

    const handleRenderSearchItem = useCallback((item: RawStoreSearchResult) => {
      return (
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0 w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
            <StoreIcon className="h-4 w-4 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">{item.NomeFantasia}</div>
            <div className="text-xs text-gray-500">{formatCNPJ(item.CNPJLojista)}</div>
          </div>
        </div>
      );
    }, []);

    if (isLoading) {
      return (
        <>
          <PageTitle>{title}</PageTitle>
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <PreloaderIcon />
              <p className="mt-4 text-sm text-gray-500">Carregando dados...</p>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <PageTitle>{title}</PageTitle>

        <div className="mx-auto space-y-6 pb-8">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-xl">
            <div className="px-6 py-8 md:px-8 md:py-10">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                  <Search className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">Buscar Lojista</h1>
                  <p className="text-blue-100 text-sm mt-1">Pesquise por nome ou CNPJ</p>
                </div>
              </div>

              <div className="relative z-50">
                <InputSearch
                  placeholder="Digite o nome fantasia ou CNPJ do lojista..."
                  onSearch={handleSearch}
                  results={results}
                  onSelect={handleSelect}
                  onRenderResultItem={handleRenderSearchItem}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center space-x-2">
                  <History className="h-5 w-5 text-gray-600" />
                  <h2 className="text-base font-semibold text-gray-900">Pesquisas Recentes</h2>
                  {history.length > 0 && (
                    <span className="ml-auto text-xs font-medium text-gray-500 bg-gray-200 px-2 py-1 rounded-full">
                      {history.length}
                    </span>
                  )}
                </div>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {history.length > 0 ? (
                  <div>
                    {history.map((store) => (
                      <StoreCard
                        key={store.CNPJLojista}
                        store={store}
                        onClick={() => handleSelect(store)}
                      />
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon={History}
                    title="Nenhuma pesquisa recente"
                    description="Suas pesquisas mais recentes aparecerão aqui para acesso rápido"
                  />
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center space-x-2">
                  <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
                  <h2 className="text-base font-semibold text-gray-900">Favoritos</h2>
                  {favorites.length > 0 && (
                    <span className="ml-auto text-xs font-medium text-gray-500 bg-gray-200 px-2 py-1 rounded-full">
                      {favorites.length}
                    </span>
                  )}
                </div>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {favorites.length > 0 ? (
                  <div>
                    {favorites.map((store) => (
                      <StoreCard
                        key={store.CNPJLojista}
                        store={store}
                        onClick={() => handleSelect(store)}
                      />
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon={Star}
                    title="Nenhum favorito"
                    description="Marque lojas como favoritas para acessá-las rapidamente"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Building2 className="h-5 w-5 text-gray-600" />
                  <h2 className="text-base font-semibold text-gray-900">Lojistas Disponíveis</h2>
                  <span className="text-xs font-medium text-gray-500 bg-white px-2 py-1 rounded-full border border-gray-200">
                    {stores.length} {stores.length === 1 ? 'lojista' : 'lojistas'}
                  </span>
                </div>
                {hasMoreStores && (
                  <button
                    onClick={() => setShowAllStores(!showAllStores)}
                    className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <span>{showAllStores ? 'Ver menos' : 'Ver todos'}</span>
                    {showAllStores ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                )}
              </div>
              {!showAllStores && hasMoreStores && (
                <p className="text-xs text-gray-500 mt-2">
                  Mostrando 20 de {stores.length} lojistas
                </p>
              )}
            </div>

            <div className="max-h-[600px] overflow-y-auto">
              {displayedStores.length > 0 ? (
                <div>
                  {displayedStores.map((store) => (
                    <StoreListItem
                      key={store.CNPJLojista}
                      store={store}
                      onClick={() => handleSelect(store)}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={Building2}
                  title="Nenhum lojista disponível"
                  description="Não há lojistas cadastrados no momento"
                />
              )}
            </div>

            {showAllStores && hasMoreStores && (
              <div className="px-5 py-4 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => setShowAllStores(false)}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronUp className="h-4 w-4" />
                  <span>Mostrar menos</span>
                </button>
              </div>
            )}
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-xl p-6">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Dica Profissional</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-600 font-bold mt-0.5">•</span>
                    <span>Use a busca para encontrar lojistas rapidamente por nome ou CNPJ</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-600 font-bold mt-0.5">•</span>
                    <span>Marque como favorito os lojistas que você mais atende</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-blue-600 font-bold mt-0.5">•</span>
                    <span>Acesse o histórico para retomar conversas anteriores</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </>
    );
}
